import { groupBy, map, sumBy, minBy } from "lodash";
import moment from "moment";
import { getOrderPrice } from "./order";
import { getLimaMoment } from "./date";
import { getTablePlace } from "./table";
import { getPaymentMethod, getDeliveryChannel, describePaymentParts } from "./constants";

// Ticket de 80 mm (ancho estándar de las impresoras térmicas de caja) —
// pre-cuenta para el cliente o ticket del cobro. NO es un comprobante de pago
// ante SUNAT (lo dice al pie), cumple el papel de la "Nota de venta" de
// Restaurant.pe. Se imprime desde un iframe oculto para no arrastrar el
// layout del Admin ni pelear con el @page de los reportes.

const money = (value) => Number(value || 0).toFixed(2);

const escapeHtml = (text) =>
  String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

function extrasTotal(order) {
  return sumBy(order.extras || [], (e) => Number(e.price || 0) * (e.quantity || 1));
}

// Agrupa las filas de Order (una por unidad) en líneas "2 × Ceviche". Los
// adicionales van en la primera fila de cada pedido (ver CLAUDE.md), así que
// se separan del precio base y se listan como líneas propias.
function buildLines(orders) {
  // Cortesía: el plato (y sus adicionales) va en 0.00, con "cortesía" en el
  // detalle; sus adicionales no se cobran como líneas aparte.
  const baseOf = (o) => (o.is_courtesy ? 0 : getOrderPrice(o) - extrasTotal(o));
  const products = map(
    groupBy(orders, (o) =>
      [o.product_data?.title, o.option_choice, money(baseOf(o)), o.offer_data?.title,
        o.is_courtesy, o.takeaway].join("|")
    ),
    (rows) => {
      const first = rows[0];
      return {
        quantity: rows.length,
        title: first.product_data?.title || "Producto",
        detail: [
          first.option_choice,
          first.offer_data && `🎁 ${first.offer_data.title}`,
          first.takeaway && "para llevar",
          first.is_courtesy &&
            `cortesía${first.courtesy_reason ? ` (${first.courtesy_reason})` : ""}`,
        ]
          .filter(Boolean)
          .join(" · "),
        amount: baseOf(first) * rows.length,
      };
    }
  );

  const extras = map(
    groupBy(
      orders.filter((o) => !o.is_courtesy).flatMap((o) => o.extras || []),
      (e) => `${e.label}|${money(e.price)}`
    ),
    (rows) => ({
      quantity: sumBy(rows, (e) => e.quantity || 1),
      title: `+ ${rows[0].label}`,
      detail: "",
      amount: sumBy(rows, (e) => Number(e.price || 0) * (e.quantity || 1)),
    })
  );

  return [...products, ...extras];
}

export function buildTicketHtml({
  siteName,
  orders,
  payment,
  guests,
  customer,
  deliveryFee,
  deliveryChannel,
  kind = "precuenta",
  // Boleta/factura electrónica ya emitida (Comprobante) y datos del emisor:
  // el ticket pasa a ser su representación impresa. En modo prueba lleva el
  // aviso de que NO tiene validez tributaria.
  comprobante,
  issuer,
  // Mesero de la mesa (pre-cuenta, antes de que exista la cuenta).
  waiter: tableWaiter,
  // "Venta por consumo" sin comprobante (ticket previo al cobro): una sola línea.
  summaryLine,
}) {
  const persons = payment?.guests || guests;
  const client = payment?.customer_data || customer;
  let lines = buildLines(orders);
  // "Venta por consumo": el documento no lista los productos, solo una línea.
  const summarized = !!(comprobante?.summarized || summaryLine);
  const summaryText = comprobante?.summarized ? comprobante.summary_text : summaryLine;
  const subtotal = sumBy(orders, getOrderPrice);
  // Descuento o cortesía aplicado al cobrar (Payment.discount_amount): el
  // TOTAL del ticket es lo que realmente se cobra.
  const discount = payment ? Number(payment.discount_amount || 0) : 0;
  // Delivery: el envío se suma aparte (los descuentos van solo sobre platos).
  const fee = Number((payment ? payment.delivery_fee : deliveryFee) || 0);
  const channelText = getDeliveryChannel(payment ? payment.delivery_channel : deliveryChannel)
    ?.text;
  const total = Math.max(0, subtotal - discount) + fee;
  if (summarized) {
    lines = [{ quantity: 1, title: summaryText || "Venta por consumo", detail: "", amount: total }];
  }
  const received = payment?.cash_received != null ? Number(payment.cash_received) : null;
  // El vuelto sale de la parte en efectivo (en un pago mixto, no del total).
  const cashDue =
    payment?.paymentType === "MIXED"
      ? (payment.parts || [])
          .filter((part) => part.method === "CASH")
          .reduce((sum, part) => sum + Number(part.amount), 0)
      : total;
  const firstOrder = minBy(orders, (o) => moment(o.created_at).valueOf());
  const waiter = payment?.waiter_name || tableWaiter || firstOrder?.created_by_name;
  const method = payment ? getPaymentMethod(payment.paymentType) : null;
  const title = comprobante
    ? String(comprobante.doc_label || "COMPROBANTE").toUpperCase()
    : kind === "pago"
    ? "TICKET DE VENTA"
    : "PRE-CUENTA";

  const rows = lines
    .map(
      (line) => `
      <tr>
        <td class="qty">${line.quantity}</td>
        <td>${escapeHtml(line.title)}${
          line.detail ? `<div class="detail">${escapeHtml(line.detail)}</div>` : ""
        }</td>
        <td class="amount">${money(line.amount)}</td>
      </tr>`
    )
    .join("");

  const html = `<!doctype html><html><head><meta charset="utf-8">
<title>${title}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  body { width: 72mm; margin: 0 auto; padding: 4mm 0; font-family: "Courier New", monospace;
         font-size: 12px; color: #000; }
  .center { text-align: center; }
  h1 { font-size: 15px; margin: 0 0 2px; }
  h2 { font-size: 13px; margin: 6px 0; letter-spacing: 1px; }
  .muted { font-size: 11px; }
  hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { vertical-align: top; padding: 2px 0; }
  .qty { width: 11mm; padding-right: 2mm; }
  .amount { text-align: right; white-space: nowrap; padding-left: 2mm; }
  .detail { font-size: 10px; }
  .total { font-size: 15px; font-weight: bold; }
  .test { border: 2px solid #000; margin: 6px 0; padding: 3px; font-weight: bold; font-size: 12px; }
</style></head><body>
  <div class="center">
    <h1>${escapeHtml(comprobante && issuer?.legal_name ? issuer.legal_name : siteName || "iCard")}</h1>
    ${
      comprobante && issuer
        ? `<div class="muted">${
            issuer.trade_name ? `${escapeHtml(issuer.trade_name)}<br>` : ""
          }${comprobante.doc_type === "NOTA" ? "" : `RUC ${escapeHtml(issuer.ruc)}<br>`}${escapeHtml(issuer.address)}</div>`
        : ""
    }
    <h2>${title}</h2>
    ${comprobante ? `<div><b>${escapeHtml(comprobante.full_number)}</b></div>` : ""}
    ${
      comprobante?.is_test
        ? `<div class="test">DOCUMENTO DE PRUEBA<br>SIN VALIDEZ TRIBUTARIA</div>`
        : ""
    }
  </div>
  <div><b>${escapeHtml(getTablePlace(firstOrder || {}))}</b></div>
  <div>Fecha: ${getLimaMoment().format("DD/MM/YYYY HH:mm")}</div>
  ${waiter ? `<div>Atendido por: ${escapeHtml(waiter)}</div>` : ""}
  ${persons ? `<div>Personas: ${Number(persons)}</div>` : ""}
  ${
    comprobante
      ? `<div>Cliente: ${escapeHtml(comprobante.customer_name || "CLIENTES VARIOS")}${
          comprobante.customer_doc_number
            ? ` · ${escapeHtml(comprobante.customer_doc_type)} ${escapeHtml(comprobante.customer_doc_number)}`
            : ""
        }</div>${
          comprobante.customer_address && comprobante.customer_address !== "-"
            ? `<div class="detail">${escapeHtml(comprobante.customer_address)}</div>`
            : ""
        }`
      : client
      ? `<div>Cliente: ${escapeHtml(client.full_name)}${
          client.doc_number
            ? ` · ${escapeHtml(client.doc_type)} ${escapeHtml(client.doc_number)}`
            : ""
        }</div>`
      : ""
  }
  <hr>
  <table>
    <tr><td class="qty"><b>Cant</b></td><td><b>Descripción</b></td><td class="amount"><b>S/</b></td></tr>
    ${rows}
  </table>
  <hr>
  <table>
    ${
      (discount > 0 || fee > 0) && !summarized
        ? `<tr><td>Subtotal</td><td class="amount">${money(subtotal)}</td></tr>`
        : ""
    }
    ${
      discount > 0 && !summarized
        ? `
    <tr><td>Descuento${
      payment.discount_type === "PERCENT" ? ` (${Number(payment.discount_value)}%)` : ""
    }${
      payment.discount_reason
        ? `<div class="detail">${escapeHtml(payment.discount_reason)}</div>`
        : ""
    }</td><td class="amount">-${money(discount)}</td></tr>`
        : ""
    }
    ${
      fee > 0 && !summarized
        ? `<tr><td>Envío${channelText ? ` (${escapeHtml(channelText)})` : ""}</td><td class="amount">${money(fee)}</td></tr>`
        : ""
    }
    ${
      comprobante && comprobante.doc_type !== "NOTA"
        ? `<tr><td>Op. gravada</td><td class="amount">${money(comprobante.taxable_amount)}</td></tr>
    <tr><td>IGV (${Number(comprobante.igv_rate)}%)</td><td class="amount">${money(comprobante.igv_amount)}</td></tr>`
        : ""
    }
    <tr><td class="total">TOTAL</td><td class="amount total">S/ ${money(comprobante ? comprobante.total : total)}</td></tr>
    ${
      received != null
        ? `<tr><td>Recibido</td><td class="amount">${money(received)}</td></tr>
    <tr><td>Vuelto</td><td class="amount">${money(Math.max(0, received - cashDue))}</td></tr>`
        : ""
    }
  </table>
  ${
    method
      ? `<div>Forma de pago: ${escapeHtml(method.text)}${
          payment.operation_code ? ` · Op. ${escapeHtml(payment.operation_code)}` : ""
        }</div>${
          payment.paymentType === "MIXED"
            ? `<div>${escapeHtml(describePaymentParts(payment.parts))}</div>`
            : ""
        }`
      : ""
  }
  <hr>
  <div class="center muted">
    ${
      comprobante
        ? comprobante.doc_type === "NOTA"
          ? "Documento interno: no es un comprobante de pago electrónico ni tiene validez tributaria."
          : comprobante.is_test
          ? "Representación impresa de un comprobante SIMULADO: no fue enviado a SUNAT y no tiene validez tributaria."
          : "Representación impresa del comprobante electrónico."
        : "Documento informativo — no es un comprobante de pago."
    }<br>
    ¡Gracias por su visita!
  </div>
</body></html>`;
  return html;
}

export function printTicket(options) {
  const html = buildTicketHtml(options);
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  // Esperar a que el iframe termine de pintar antes de imprimir.
  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => iframe.remove(), 1000);
  }, 250);
}
